import { useState, useEffect, useCallback } from 'react';
import * as Sentry from '@sentry/react';
import { supabase } from '@/lib/supabaseClient';
import { toast } from 'sonner';

/* Rango del año en curso: '2025-01-01' / '2025-12-31' */
const getAnioRango = () => {
  const year = new Date().getFullYear();
  return { inicio: `${year}-01-01`, fin: `${year}-12-31` };
};

/**
 * Devuelve los días no laborables (RPC) para un usuario dado.
 */
export function useDiasNoLaborablesPorUsuario(
  idUsuario: string | null,
  idEmpresa: string | null
) {
  const [diasNoLaborables, setDiasNoLaborables] = useState<string[]>([]);

  const fetchDiasNoLaborables = useCallback(async () => {
    if (!idUsuario) return;

    const { inicio, fin } = getAnioRango();

    const { data, error } = await supabase.rpc(
      'obtener_dias_no_laborables_trabajador',
      {
        p_id_usuario: idUsuario,
        p_id_empresa: idEmpresa,
        p_fecha_inicio: inicio,
        p_fecha_fin: fin,
      }
    );

    if (error) {
      Sentry.captureException(error, { extra: { idUsuario, idEmpresa } }); // 👈
      toast.error('Error al cargar días no laborables');
    } else {
      setDiasNoLaborables((data as any[]).map((d) => d.fecha));
    }
  }, [idUsuario, idEmpresa]);

  useEffect(() => {
    fetchDiasNoLaborables();
  }, [fetchDiasNoLaborables]);

  return diasNoLaborables;
}

/**
 * Devuelve los días no laborables (RPC) para una empresa dada.
 */
export function useDiasNoLaborablesPorEmpresa(idEmpresa: string) {
  const [diasNoLaborables, setDiasNoLaborables] = useState<string[]>([]);

  const fetchDiasNoLaborables = useCallback(async () => {
    if (!idEmpresa) return;

    const { inicio, fin } = getAnioRango();

    const { data, error } = await supabase.rpc(
      'obtener_dias_no_laborables_empresa',
      {
        p_id_empresa: idEmpresa,
        p_fecha_inicio: inicio,
        p_fecha_fin: fin,
      }
    );

    if (error) {
      Sentry.captureException(error, { extra: { idEmpresa } }); // 👈
      toast.error('Error al cargar días no laborables');
    } else {
      setDiasNoLaborables((data as any[]).map((d) => d.fecha));
    }
  }, [idEmpresa]);

  useEffect(() => {
    fetchDiasNoLaborables();
  }, [fetchDiasNoLaborables]);

  return diasNoLaborables;
}
